import { Fragment, PureComponent, ReactNode } from 'react';
import { Button, Checkbox, Input } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { motion } from 'framer-motion';
import Hotkeys from 'react-hot-keys';
import AntAutoCompleteV2 from './padV2/AntAutoComplete';
import { onFormSearch } from './padV2/onFormSearch';
import ValidationItem from './ValidationItem';
import { CodedMedications } from '../../../../api/fetchDrugSearch';
import { apiDrugToMedicationEntity } from './PrescribeAutoSuggestV2';
import { ADD_DRUG_DIALOG, MDB } from '../../trackSource';
import { track } from '../../../../tracking';
import { MedicineCategory, MedicationsEntity } from '../../../../store/types/prescription';
import { LOCAL_ID_PREFIX } from '../../../../constants';
import { onGenericCompositionSearch } from './padV2/onGenericCompositionSearch';
import { FORM_TO_DF_MAPPING } from './mxProperties';
import { onManufacturerSearch } from './padV2/onManufacturerSearch';
import { notMedicineObj } from '../../../../pages/profile/GenieConfiguration';
import { notADrugObj } from '../../../../pages/profile/ListGenieItems';
import { EkaFocosableElement } from '../../../common/types/EkaFocusable';

type State<T> = {
    drug: T;
};

export type AddDrugDialogProps<T> = {
    title?: string;
    drug: T;
    onSelect?: (d?: T, matchingDrugs?: CodedMedications[]) => void;
    onChange?: (d: T, matchingDrugs?: CodedMedications[]) => void;
    onEdit?: () => void;
    _source?: 'INLINE' | 'AS';
    hideActions?: boolean;
    formAutoFocus?: boolean;
    dropdownContainerSelector?: string;
    labelForTracking?: string;
};

interface Props<T> extends AddDrugDialogProps<T> {
    matchingDrugs?: CodedMedications[];
}

export const ADD_DRUG = 'add-drug';

class AddDrugDialog<T extends MedicationsEntity> extends PureComponent<Props<T>, State<T>> {
    manufacturer_select?: EkaFocosableElement | null = null;
    generic_select: EkaFocosableElement | null = null;

    constructor(props: Props<T>) {
        super(props);
        this.state = {
            drug: this.props.drug,
        };
    }

    componentDidMount(): void {
        if (this.props._source) {
            track('ADD_DRUG_DIALOG', { action: 'MOUNT', source: this.props._source });
        }
    }

    onFormChange = (form?: string): void => {
        const newDrug = {
            ...this.state.drug,
            form: form,
            dosage_form: form,
            df_id: form ? FORM_TO_DF_MAPPING[form] : undefined,
        };
        this.setState({ drug: newDrug });

        const { onChange } = this.props;
        if (onChange) {
            onChange(newDrug);
        }

        if (form) {
            this.generic_select?.focus();
        }
    };

    onOTCChange = (e: CheckboxChangeEvent): void => {
        const newDrug = { ...this.state.drug, is_otc: e.target.checked };

        this.setState({ drug: newDrug });

        const { onChange } = this.props;
        if (onChange) {
            onChange(newDrug);
        }
    };

    onGenericNameChange = (generic?: { id: string; name: string }): void => {
        const newDrug = {
            ...this.state.drug,
            generic_name: generic?.name,
            generic_id: generic?.id,
        };

        this.setState({ drug: newDrug });

        const { onChange } = this.props;
        if (onChange) {
            onChange(newDrug);
        }

        if (generic?.name) {
            this.manufacturer_select?.focus();
        }
    };

    onManufacturerNameChange = (manufacturer?: { id: string; name: string }): void => {
        const newDrug = {
            ...this.state.drug,
            manufacturer_name: manufacturer?.name,
            manufacturer_id: manufacturer?.id,
        };

        this.setState({ drug: newDrug });

        const { onChange } = this.props;
        if (onChange) {
            onChange(newDrug);
        }
    };

    onSuggestedSelect = (med: CodedMedications, index: number): void => {
        const { onSelect, labelForTracking } = this.props;
        const { drug } = this.state;

        const apiToMed = apiDrugToMedicationEntity(med);

        const newDrug = {
            ...apiToMed,
            ...drug,
            id: med.id,
            name: med.name,
            form: '',
            dosage_form: med.dosage?.dosage_form || '',
            df_id: med.dosage?.df_id,
            generic_id: med.generic_id,
            generic_name: med.generic_name,
            product_type: med.product_type,
            soa: med.soa || false,
            manufacturer_name: med.manufacturer_name,
            replaced_from: drug,
            track: {
                source: MDB,
                label: ADD_DRUG_DIALOG,
                index,
            },
            replaced_track: {
                label: labelForTracking,
                index,
            },
        };

        if (drug.df_id === med.dosage.df_id) {
            newDrug.dose = drug.dose;
        } else {
            newDrug.dose = apiToMed.dose;
        }

        if (onSelect) {
            track('ADD_DRUG_DIALOG', {
                action: 'SUGGESTED_DRUG_SELECTED',
                index: index.toString(),
                labelForTracking,
            });
            onSelect(newDrug as T, this.props.matchingDrugs);
        }

        const { onChange } = this.props;
        if (onChange) {
            track('ADD_DRUG_DIALOG', {
                action: 'SUGGESTED_DRUG_SELECTED',
                index: index.toString(),
                labelForTracking,
            });
            onChange(newDrug as T);
        }
    };

    onNotADrugSelect = (med_cat: MedicineCategory): void => {
        const { onSelect, labelForTracking } = this.props;
        const { drug } = this.state;

        const newDrug = {
            ...drug,
            med_cat,
        };

        this.setState({ drug: newDrug });

        if (onSelect) {
            track('ADD_DRUG_DIALOG', {
                action: 'NOT_A_DRUG_SELECT',
                med_cat,
                labelForTracking,
            });
            onSelect(newDrug as T, this.props.matchingDrugs);
        }

        const { onChange } = this.props;
        if (onChange) {
            track('ADD_DRUG_DIALOG', {
                action: 'NOT_A_DRUG_SELECT',
                med_cat,
                labelForTracking,
            });
            onChange(newDrug as T);
        }
    };

    onSave = (): void => {
        const { onSelect } = this.props;
        if (!onSelect) {
            return;
        }

        track('ADD_DRUG_DIALOG', { action: 'SAVE' });
        onSelect(this.state.drug, this.props.matchingDrugs);
    };

    cancel = (): void => {
        const { onSelect } = this.props;
        if (!onSelect) {
            return;
        }

        track('ADD_DRUG_DIALOG', { action: 'CANCEL' });
        onSelect();
    };

    close = (): void => {
        const { onSelect } = this.props;
        if (!onSelect) {
            return;
        }

        track('ADD_DRUG_DIALOG', { action: 'CLOSE' });

        onSelect();
    };

    onGenericPaste = (): void => {
        track('PASTE', { on: 'ADD_DRUG_DIALOG_GENERIC' });
    };

    onEdit = (): void => {
        const { onEdit } = this.props;
        if (!onEdit) {
            return;
        }

        track('ADD_DRUG_DIALOG', { action: 'EDIT' });
        onEdit();
    };

    render(): ReactNode {
        const {
            onSelect,
            onEdit,
            title,
            formAutoFocus = true,
            matchingDrugs,
            dropdownContainerSelector,
        } = this.props;
        const { drug } = this.state;

        const isFormValid = (drug.dosage_form || drug.form || '')?.length > 0;
        const isGenericValid = (drug.generic_name || '')?.length > 0;

        const isInfoFilled = (isFormValid && isGenericValid) || drug.is_otc || drug.med_cat;

        const notMedicineStatus = matchingDrugs && matchingDrugs[0]?.non_medicine_status;

        return (
            <Hotkeys keyName="esc" onKeyDown={this.close}>
                <motion.div
                    id={ADD_DRUG}
                    className="w-full select-none overflow-hidden"
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                >
                    <div
                        className={`flex justify-between w-full items-center py-12 px-24  ${
                            isInfoFilled
                                ? 'bg-darwin-accent-green-100'
                                : 'bg-darwin-status-warning-100'
                        }`}
                    >
                        <div className="space-x-8 flex items-center">
                            <div
                                className={
                                    'flex items-center justify-center h-26 w-26 bg-darwin-accent-medicines-pink-200 rounded-full'
                                }
                            >
                                <p className="font-700 text-darwin-accent-medicines-pink-800">Rx</p>
                            </div>
                            <span className="font-700 text-16">{title || 'Add drug'}</span>
                            {drug.med_cat && (
                                <span className="capitalize">({notMedicineObj[drug.med_cat]})</span>
                            )}
                            {isInfoFilled && (
                                <svg
                                    width="14"
                                    height="15"
                                    viewBox="0 0 14 15"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM10.0898 6.46484L6.58984 9.96484C6.31641 10.2383 5.90625 10.2383 5.66016 9.96484L3.91016 8.21484C3.63672 7.96875 3.63672 7.55859 3.91016 7.3125C4.15625 7.03906 4.56641 7.03906 4.83984 7.3125L6.125 8.59766L9.16016 5.53516C9.40625 5.28906 9.81641 5.28906 10.0625 5.53516C10.3359 5.80859 10.3359 6.21875 10.0625 6.46484H10.0898Z"
                                        fill="#1B9C7D"
                                    />
                                </svg>
                            )}
                        </div>
                        <div className="flex space-x-24">
                            {!notMedicineStatus && !drug.med_cat && (
                                <Checkbox
                                    onChange={this.onOTCChange}
                                    value={drug.is_otc || false}
                                    defaultChecked={drug.is_otc}
                                    className="text-14 text-neutral-1000 font-400"
                                >
                                    Mark as OTC
                                </Checkbox>
                            )}
                            <ValidationItem isValid={isFormValid} text="Form" />
                            <ValidationItem isValid={isGenericValid} text="Generic" />
                        </div>
                    </div>
                    <div className="flex space-between space-x-16 bg-white p-16">
                        <span className="flex items-center space-x-4 w-full">
                            <Input
                                value={drug.name}
                                className="rounded-8 px-8"
                                style={{ width: 'calc(100% - 20px)' }}
                                disabled
                            />
                            {onEdit && (
                                <div className="py-4 flex items-center" onClick={this.onEdit}>
                                    <svg
                                        width="10"
                                        height="11"
                                        viewBox="0 0 10 11"
                                        className={`w-16 h-16 ${'fill-neutral-5'} cursor-pointer`}
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path d="M9.19922 0.679688C8.76953 0.25 8.08594 0.25 7.65625 0.679688L7.07031 1.26562L8.98438 3.17969L9.57031 2.59375C10 2.16406 10 1.48047 9.57031 1.05078L9.19922 0.679688ZM3.35938 4.97656C3.24219 5.09375 3.14453 5.25 3.08594 5.40625L2.51953 7.14453C2.46094 7.30078 2.5 7.49609 2.63672 7.61328C2.75391 7.75 2.94922 7.78906 3.10547 7.73047L4.84375 7.16406C5 7.10547 5.15625 7.00781 5.27344 6.89062L8.53516 3.62891L6.62109 1.71484L3.35938 4.97656ZM1.875 1.5C0.839844 1.5 0 2.33984 0 3.375V8.375C0 9.41016 0.839844 10.25 1.875 10.25H6.875C7.91016 10.25 8.75 9.41016 8.75 8.375V6.5C8.75 6.16797 8.45703 5.875 8.125 5.875C7.77344 5.875 7.5 6.16797 7.5 6.5V8.375C7.5 8.72656 7.20703 9 6.875 9H1.875C1.52344 9 1.25 8.72656 1.25 8.375V3.375C1.25 3.04297 1.52344 2.75 1.875 2.75H3.75C4.08203 2.75 4.375 2.47656 4.375 2.125C4.375 1.79297 4.08203 1.5 3.75 1.5H1.875Z" />
                                    </svg>
                                </div>
                            )}
                        </span>
                        {!drug.id.startsWith(LOCAL_ID_PREFIX) ? (
                            <Input value={drug.dosage_form || drug.form} placeholder="Form" />
                        ) : (
                            <AntAutoCompleteV2
                                autoFocus={formAutoFocus && !isFormValid}
                                placement={formAutoFocus && !isFormValid ? 'bottomLeft' : undefined}
                                onSearch={onFormSearch}
                                selectedValue={drug.dosage_form || drug.form}
                                debounceDelay={200}
                                getLabelToCompare={(o) => o}
                                placeholder={'Form'}
                                onSelect={(option) => this.onFormChange(option)}
                                containerSelectorToAttachDropdown={dropdownContainerSelector}
                                key={drug.id}
                            />
                        )}

                        {!drug.id.startsWith(LOCAL_ID_PREFIX) ? (
                            <Input value={drug.generic_name} placeholder="Generic" />
                        ) : (
                            <AntAutoCompleteV2
                                autoFocus={isFormValid && !isGenericValid}
                                setRef={(ref) => (this.generic_select = ref)}
                                placement={formAutoFocus && !isFormValid ? 'bottomLeft' : undefined}
                                onSearch={onGenericCompositionSearch}
                                selectedValue={
                                    drug.generic_name
                                        ? {
                                              name: drug.generic_name || '',
                                              id: drug.generic_id || '',
                                          }
                                        : undefined
                                }
                                debounceDelay={200}
                                getLabelToCompare={(o) => o.name}
                                placeholder={'Generic'}
                                onSelect={(option) => this.onGenericNameChange(option)}
                                containerSelectorToAttachDropdown={dropdownContainerSelector}
                            />
                        )}

                        {!drug.id.startsWith(LOCAL_ID_PREFIX) ? (
                            <Input value={drug.manufacturer_name} placeholder="Manufacturer" />
                        ) : (
                            <AntAutoCompleteV2
                                setRef={(ref) => (this.manufacturer_select = ref)}
                                placement={formAutoFocus && !isFormValid ? 'bottomLeft' : undefined}
                                onSearch={onManufacturerSearch}
                                selectedValue={
                                    drug.manufacturer_name
                                        ? {
                                              name: drug.manufacturer_name || '',
                                              id: drug.manufacturer_id || '',
                                          }
                                        : undefined
                                }
                                debounceDelay={200}
                                getLabelToCompare={(o) => o.name}
                                placeholder={'Manufacturer'}
                                onSelect={(option) => this.onManufacturerNameChange(option)}
                                containerSelectorToAttachDropdown={dropdownContainerSelector}
                            />
                        )}
                    </div>
                    {onSelect || (matchingDrugs && matchingDrugs?.length > 0) ? (
                        <div className="flex justify-between py-8 px-16 overflow-hidden w-full items-center bg-darwin-neutral-50">
                            <div className="overflow-hidden w-full">
                                {matchingDrugs?.length ? (
                                    <div className="flex items-center space-x-16 overflow-hidden w-full">
                                        <span className="text-darwin-neutral-900 italic">
                                            Did you mean
                                        </span>

                                        <div className="flex-1 flex overflow-x-auto space-x-8">
                                            {notMedicineStatus ? (
                                                <Fragment>
                                                    <div
                                                        className="font-400 text-13 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                        onClick={() =>
                                                            this.onNotADrugSelect(notMedicineStatus)
                                                        }
                                                    >
                                                        It's {notADrugObj[notMedicineStatus]}
                                                    </div>

                                                    <div
                                                        className="font-400 text-13 text-darwin-primary-500 whitespace-nowrap cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                        onClick={() => this.onNotADrugSelect('nad')}
                                                    >
                                                        Not a Drug
                                                    </div>
                                                </Fragment>
                                            ) : (
                                                matchingDrugs.map((med, index) => (
                                                    <div
                                                        key={med.id}
                                                        onClick={() =>
                                                            this.onSuggestedSelect(med, index)
                                                        }
                                                        className="cursor-pointer rounded-8 border-darwin-primary-500 border-1 py-6 px-16"
                                                    >
                                                        <div className="flex space-x-12 items-center">
                                                            <span className="whitespace-nowrap font-400 text-13 text-darwin-primary-500">
                                                                {med.name}
                                                            </span>
                                                            {med.dosage?.dosage_form && (
                                                                <span className="whitespace-nowrap text-darwin-neutral-700 px-4 py-2 rounded-4 border border-darwin-neutral-200 text-10">
                                                                    {med.dosage?.dosage_form}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {med.generic_name?.trim() && (
                                                            <span className="max-w-400 font-400 text-12 text-darwin-neutral-600 mt-2 truncate-overflow-2">
                                                                {med.generic_name}
                                                            </span>
                                                        )}
                                                        {med.manufacturer_name?.trim() && (
                                                            <span className="max-w-400 font-400 text-11 text-darwin-neutral-500 mt-2 truncate-overflow-2">
                                                                Mfr: {med.manufacturer_name}
                                                            </span>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex space-x-24">
                                {onSelect && (
                                    <Button
                                        disabled={!drug.name}
                                        type="text"
                                        onClick={this.cancel}
                                        className={'text-darwin-primary-500 text-13'}
                                    >
                                        Cancel
                                    </Button>
                                )}
                                {onSelect && (
                                    <Button
                                        disabled={!drug.name}
                                        className="primary-btn font-lato"
                                        onClick={this.onSave}
                                        type="primary"
                                    >
                                        Add Drug
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : null}
                </motion.div>
            </Hotkeys>
        );
    }
}

export default AddDrugDialog;
